import { Container, Graphics, Text } from 'pixi.js';
import { Easings, TweenGroup } from '../../core/Tween';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface PlayerTokenOptions {
  /** Visible diameter in px. */
  size: number;
  /** Fill colour. Default brand pink. */
  fillColor?: number;
  /** Inner glyph (typically a player avatar shorthand). */
  glyph?: string;
}

/**
 * Animated player token — a coloured disc with an inner glyph. Hops from
 * tile to tile via {@link hopTo} with a small arc so the movement reads
 * as "step" rather than "slide".
 *
 * <p>Coordinate space is the host {@link Container}'s — typically a
 * {@code BoardWidget} positions tiles + the token in shared coords.
 */
export class PlayerToken extends Container {
  private readonly disc: Graphics;
  private readonly glyphText: Text;
  private readonly tweens = new TweenGroup();

  constructor(opts: PlayerTokenOptions) {
    super();

    const fill = opts.fillColor ?? 0xff6bd6;

    this.disc = new Graphics();
    this.disc
      .circle(0, 0, opts.size / 2)
      .fill({ color: fill, alpha: 0.95 })
      .stroke({ color: 0xffffff, alpha: 0.85, width: 3 });
    this.addChild(this.disc);

    this.glyphText = new Text({
      text: opts.glyph ?? '🕵️',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(opts.size * 0.55),
        align: 'center',
      },
    });
    this.glyphText.anchor.set(0.5);
    this.addChild(this.glyphText);
  }

  /** Snap to a position without animation. Used for resets / first layout. */
  snapTo(x: number, y: number): void {
    this.x = x;
    this.y = y;
  }

  /**
   * Hop to a new position with a small arc. Resolves when the hop
   * completes — host scenes await each hop in sequence to play
   * step-by-step movement on a multi-tile roll.
   */
  hopTo(targetX: number, targetY: number, durationMs: number = 280): Promise<void> {
    const startX = this.x;
    const startY = this.y;
    const arcHeight = Math.max(12, durationMs * 0.08);
    return this.tweens.startAsync({
      durationMs,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        this.x = startX + (targetX - startX) * p;
        // Up-then-down arc layered on top of linear y interpolation.
        const linearY = startY + (targetY - startY) * p;
        this.y = linearY - Math.sin(p * Math.PI) * arcHeight;
      },
    });
  }

  destroyToken(): void {
    this.tweens.cancelAll();
  }
}
